/**
 * Registry of investment calculators (SIP, lump-sum, FD, compound interest).
 * One config-driven widget + a dynamic $calc route render any entry. All strings
 * are Paraglide `m.*` (English authored now). Defaults give a populated example
 * result on first render (deterministic → no CLS / hydration mismatch).
 */
import { m } from "@/paraglide/messages";
import { compoundInterest, lumpSum, sip } from "./finance";

export type ResultKind = "currency" | "percent" | "number";

export interface ResultItem {
  label: () => string;
  value: number;
  kind: ResultKind;
}

export interface FieldDef {
  id: string;
  label: () => string;
  /** Shown after the input, e.g. "% p.a." or "years". */
  suffix?: string;
  defaultValue?: string;
}

export interface CalcConfig {
  fields: FieldDef[];
  compute: (values: Record<string, number>) => ResultItem[] | null;
}

export interface FaqItem {
  q: () => string;
  a: () => string;
}

export interface FinanceCalcDef {
  slug: string;
  path: string;
  icon: string;
  navName: () => string;
  metaTitle: () => string;
  metaDescription: () => string;
  h1: () => string;
  intro: () => string;
  config: CalcConfig;
  howTo: () => string;
  faqs: FaqItem[];
}

const BASE = "/sip-calculator";

const rateField: FieldDef = {
  id: "rate",
  label: () => m.sip_field_rate(),
  suffix: "% p.a.",
  defaultValue: "12",
};
const yearsField: FieldDef = {
  id: "years",
  label: () => m.sip_field_years(),
  suffix: "yr",
  defaultValue: "10",
};

export const CALCULATORS: Record<string, FinanceCalcDef> = {
  "lump-sum-calculator": {
    slug: "lump-sum-calculator",
    path: `${BASE}/lump-sum-calculator`,
    icon: "💰",
    navName: () => m.sip_lumpsum_nav(),
    metaTitle: () => m.sip_lumpsum_meta_title(),
    metaDescription: () => m.sip_lumpsum_meta_description(),
    h1: () => m.sip_lumpsum_h1(),
    intro: () => m.sip_lumpsum_intro(),
    config: {
      fields: [
        {
          id: "principal",
          label: () => m.sip_field_lumpsum(),
          defaultValue: "100000",
        },
        rateField,
        yearsField,
      ],
      compute: (v) => {
        const r = lumpSum(v.principal, v.rate, v.years);
        return r === null
          ? null
          : [
              {
                label: () => m.sip_result_future_value(),
                value: r.futureValue,
                kind: "currency",
              },
              {
                label: () => m.sip_result_invested(),
                value: r.invested,
                kind: "currency",
              },
              {
                label: () => m.sip_result_returns(),
                value: r.returns,
                kind: "currency",
              },
            ];
      },
    },
    howTo: () => m.sip_lumpsum_howto(),
    faqs: [
      { q: () => m.sip_lumpsum_faq1_q(), a: () => m.sip_lumpsum_faq1_a() },
      { q: () => m.sip_lumpsum_faq2_q(), a: () => m.sip_lumpsum_faq2_a() },
      { q: () => m.sip_lumpsum_faq3_q(), a: () => m.sip_lumpsum_faq3_a() },
      { q: () => m.sip_lumpsum_faq4_q(), a: () => m.sip_lumpsum_faq4_a() },
    ],
  },

  "fd-calculator": {
    slug: "fd-calculator",
    path: `${BASE}/fd-calculator`,
    icon: "🏦",
    navName: () => m.sip_fd_nav(),
    metaTitle: () => m.sip_fd_meta_title(),
    metaDescription: () => m.sip_fd_meta_description(),
    h1: () => m.sip_fd_h1(),
    intro: () => m.sip_fd_intro(),
    config: {
      fields: [
        {
          id: "principal",
          label: () => m.sip_field_deposit(),
          defaultValue: "100000",
        },
        rateField,
        yearsField,
      ],
      // FD interest compounds quarterly.
      compute: (v) => {
        const r = lumpSum(v.principal, v.rate, v.years, 4);
        return r === null
          ? null
          : [
              {
                label: () => m.sip_result_maturity(),
                value: r.futureValue,
                kind: "currency",
              },
              {
                label: () => m.sip_result_invested(),
                value: r.invested,
                kind: "currency",
              },
              {
                label: () => m.sip_result_interest(),
                value: r.returns,
                kind: "currency",
              },
            ];
      },
    },
    howTo: () => m.sip_fd_howto(),
    faqs: [
      { q: () => m.sip_fd_faq1_q(), a: () => m.sip_fd_faq1_a() },
      { q: () => m.sip_fd_faq2_q(), a: () => m.sip_fd_faq2_a() },
      { q: () => m.sip_fd_faq3_q(), a: () => m.sip_fd_faq3_a() },
      { q: () => m.sip_fd_faq4_q(), a: () => m.sip_fd_faq4_a() },
    ],
  },

  "compound-interest-calculator": {
    slug: "compound-interest-calculator",
    path: `${BASE}/compound-interest-calculator`,
    icon: "📈",
    navName: () => m.sip_compound_nav(),
    metaTitle: () => m.sip_compound_meta_title(),
    metaDescription: () => m.sip_compound_meta_description(),
    h1: () => m.sip_compound_h1(),
    intro: () => m.sip_compound_intro(),
    config: {
      fields: [
        {
          id: "principal",
          label: () => m.sip_field_principal(),
          defaultValue: "100000",
        },
        rateField,
        yearsField,
        {
          id: "freq",
          label: () => m.sip_field_frequency(),
          suffix: "/yr",
          defaultValue: "1",
        },
      ],
      compute: (v) => {
        const r = compoundInterest(v.principal, v.rate, v.years, v.freq);
        return r === null
          ? null
          : [
              {
                label: () => m.sip_result_amount(),
                value: r.amount,
                kind: "currency",
              },
              {
                label: () => m.sip_result_interest(),
                value: r.interest,
                kind: "currency",
              },
            ];
      },
    },
    howTo: () => m.sip_compound_howto(),
    faqs: [
      { q: () => m.sip_compound_faq1_q(), a: () => m.sip_compound_faq1_a() },
      { q: () => m.sip_compound_faq2_q(), a: () => m.sip_compound_faq2_a() },
      { q: () => m.sip_compound_faq3_q(), a: () => m.sip_compound_faq3_a() },
      { q: () => m.sip_compound_faq4_q(), a: () => m.sip_compound_faq4_a() },
    ],
  },
};

export const CALCULATOR_LIST: FinanceCalcDef[] = Object.values(CALCULATORS);

/** Safe lookup (`Object.hasOwn` so inherited keys don't resolve and 500). */
export function getCalculator(slug: string): FinanceCalcDef | undefined {
  return Object.hasOwn(CALCULATORS, slug) ? CALCULATORS[slug] : undefined;
}

/** The hub (SIP) calculator config — used directly by the index page. */
export const SIP_CONFIG: CalcConfig = {
  fields: [
    { id: "monthly", label: () => m.sip_field_monthly(), defaultValue: "5000" },
    rateField,
    yearsField,
  ],
  compute: (v) => {
    const r = sip(v.monthly, v.rate, v.years);
    return r === null
      ? null
      : [
          {
            label: () => m.sip_result_future_value(),
            value: r.futureValue,
            kind: "currency",
          },
          {
            label: () => m.sip_result_invested(),
            value: r.invested,
            kind: "currency",
          },
          {
            label: () => m.sip_result_returns(),
            value: r.returns,
            kind: "currency",
          },
        ];
  },
};
